import { useCallback, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { applyOptimistic, rollbackOptimistic } from '@/lib/optimisticMutation';
import { toastError } from '@/lib/toast';

/**
 * Reordenamiento por arrastre de una lista cacheada por react-query
 * (admin-list-drag-reorder): `moveRow` reordena el caché en vivo mientras se
 * arrastra (drop en `DraggableRow`), y `onDrop` persiste solo las filas cuyo
 * índice cambió, revirtiendo el caché si el servidor rechaza el cambio.
 */
export function useDragReorder<T extends { id: string; orderIndex: number }>(
  queryKey: QueryKey,
  saveOrder: (id: string, orderIndex: number) => Promise<unknown>,
) {
  const qc = useQueryClient();
  const snapshotRef = useRef<T[] | undefined>(undefined);

  const onDragStart = useCallback(() => {
    if (snapshotRef.current) return; // ya hay un drag en curso (no debería pasar, pero es idempotente)
    void applyOptimistic<T[]>(qc, queryKey, (prev) => prev).then((snapshot) => {
      snapshotRef.current = snapshot;
    });
  }, [qc, queryKey]);

  const moveRow = useCallback((dragIndex: number, hoverIndex: number) => {
    qc.setQueryData<T[]>(queryKey, (prev) => {
      if (!prev) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, moved);
      return next;
    });
  }, [qc, queryKey]);

  const onDrop = useCallback(async () => {
    const snapshot = snapshotRef.current;
    snapshotRef.current = undefined;
    const current = qc.getQueryData<T[]>(queryKey);
    if (!snapshot || !current) return;

    const changed = current
      .map((row, index) => ({ row, index }))
      .filter(({ row, index }) => snapshot.find((s) => s.id === row.id)?.orderIndex !== index);
    if (changed.length === 0) return;

    try {
      await Promise.all(changed.map(({ row, index }) => saveOrder(row.id, index)));
      qc.setQueryData<T[]>(queryKey, (prev) => prev?.map((row, index) => ({ ...row, orderIndex: index })));
    } catch {
      rollbackOptimistic(qc, queryKey, snapshot);
      toastError('No se pudo guardar el nuevo orden.');
    }
  }, [qc, queryKey, saveOrder]);

  /** Sube (-1) o baja (+1) una fila sin arrastrar: mismo guardado y mismo rollback que el arrastre. */
  const moveBy = useCallback(async (index: number, delta: -1 | 1) => {
    const list = qc.getQueryData<T[]>(queryKey);
    const target = index + delta;
    if (!list || target < 0 || target >= list.length) return;
    snapshotRef.current = list;
    moveRow(index, target);
    await onDrop();
  }, [qc, queryKey, moveRow, onDrop]);

  return { moveRow, onDragStart, onDrop, moveBy };
}
